import {
  ContactHistoryCategoryChangedPayload,
  ContactHistoryCommunicationPayload,
  ContactHistoryFieldChangedPayload,
  ContactHistoryKind,
  ContactHistoryNoteChangedPayload,
} from '@/api/address-book/types';
import type { ContactHistoryEntry } from '@/api/address-book-bff/types';
import { formatContactAddress } from '@/utils/format-contact-address';

import { actorName, isCommunicationKind } from './constants';

const FIELD_LABELS: Record<string, string> = {
  name: 'name',
  email: 'email',
  phone: 'phone',
  company: 'company',
  jobTitle: 'job title',
  affiliation: 'affiliation',
  website: 'website',
  country: 'country',
  city: 'city',
  address: 'address',
  socialLinks: 'social links',
  'value.Email': 'emails',
  'value.Phone': 'phones',
  'value.Address': 'addresses',
  'value.Website': 'websites',
  'value.Social': 'social links',
};

const ADDRESS_FIELDS = new Set(['address', 'value.Address']);
const PHOTO_FIELD = 'iconId';

const humanizeField = (field: string): string =>
  FIELD_LABELS[field] ??
  field
    .replace(/^value\./, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .toLowerCase();

export const formatChangedValue = (
  to: string | string[] | null | undefined,
  format: (value: string) => string = (value) => value,
): string | null => {
  if (to == null) return null;
  const values = (Array.isArray(to) ? to : [to])
    .map((value) => format(value).trim())
    .filter(Boolean);
  return values.length > 0 ? values.join(', ') : null;
};

const isFieldPayload = (payload: unknown): payload is ContactHistoryFieldChangedPayload =>
  typeof payload === 'object' && payload !== null && 'field' in payload;

const isCategoryPayload = (payload: unknown): payload is ContactHistoryCategoryChangedPayload =>
  typeof payload === 'object' && payload !== null && 'rootName' in payload;

const isNotePayload = (payload: unknown): payload is ContactHistoryNoteChangedPayload =>
  typeof payload === 'object' && payload !== null && 'to' in payload;

const isCommunicationPayload = (payload: unknown): payload is ContactHistoryCommunicationPayload =>
  typeof payload === 'object' && payload !== null && 'fileIds' in payload;

export const communicationTextOf = (entry: ContactHistoryEntry): string | null =>
  isCommunicationKind(entry.kind) && isCommunicationPayload(entry.payload)
    ? entry.payload.text
    : null;

export const communicationFileIdsOf = (entry: ContactHistoryEntry): string[] =>
  isCommunicationKind(entry.kind) && isCommunicationPayload(entry.payload)
    ? entry.payload.fileIds
    : [];

export const isEdited = (entry: Pick<ContactHistoryEntry, 'createdAt' | 'updatedAt'>): boolean =>
  Date.parse(entry.updatedAt) > Date.parse(entry.createdAt);

export interface SystemEntryDescription {
  actor: string;
  action: string;
  value: string | null;
  body?: string;
}

const describeFieldChange = (
  actor: string,
  payload: ContactHistoryFieldChangedPayload | null,
): SystemEntryDescription => {
  if (!payload) return { actor, action: 'changed a field', value: null };

  if (payload.field === PHOTO_FIELD) {
    return { actor, action: 'changed the photo', value: null };
  }

  const label = payload.label ?? humanizeField(payload.field);
  const format = ADDRESS_FIELDS.has(payload.field) ? formatContactAddress : undefined;
  const value = formatChangedValue(payload.to, format);

  if (value !== null) return { actor, action: `changed the ${label} to`, value };
  if (Array.isArray(payload.to)) return { actor, action: `removed all ${label}`, value: null };
  return { actor, action: `cleared the ${label}`, value: null };
};

const describeNoteChange = (
  actor: string,
  payload: ContactHistoryNoteChangedPayload | null,
): SystemEntryDescription => {
  const text = typeof payload?.to === 'string' ? payload.to.trim() : '';
  return text
    ? { actor, action: 'changed the About', value: null, body: text }
    : { actor, action: 'cleared the About', value: null };
};

const describeCategoryChange = (
  actor: string,
  payload: ContactHistoryCategoryChangedPayload | null,
): SystemEntryDescription => {
  if (!payload) return { actor, action: 'changed a category', value: null };

  const label = payload.nodeName ?? payload.rootName;
  const value = formatChangedValue(payload.to);
  return value !== null
    ? { actor, action: `changed ${label} to`, value }
    : { actor, action: `cleared ${label}`, value: null };
};

export const describeSystemEntry = (entry: ContactHistoryEntry): SystemEntryDescription => {
  const actor = actorName(entry.actor);

  switch (entry.kind) {
    case ContactHistoryKind.FieldChanged:
      return describeFieldChange(actor, isFieldPayload(entry.payload) ? entry.payload : null);
    case ContactHistoryKind.NoteChanged:
      return describeNoteChange(actor, isNotePayload(entry.payload) ? entry.payload : null);
    case ContactHistoryKind.CategoryChanged:
      return describeCategoryChange(actor, isCategoryPayload(entry.payload) ? entry.payload : null);
    default:
      return { actor, action: 'updated this contact', value: null };
  }
};

export const describeSystemGroup = (entries: ContactHistoryEntry[]): SystemEntryDescription => ({
  actor: actorName(entries[0]?.actor),
  action: `updated ${entries.length} fields`,
  value: null,
});
